/**
 * All network access for the player, on fetch (no axios, no XHR patching).
 * Pollers have jitter + error backoff so a big audience doesn't hammer the
 * status endpoints in lockstep.
 */

const WATCH_API = import.meta.env.VITE_WATCH_API_ENDPOINT ?? 'https://watch-guard.livepush.io/v1'
const STATUS_API = import.meta.env.VITE_STATUS_ENDPOINT ?? 'https://state.livepush.io/v1'
const VIEWERS_API = import.meta.env.VITE_VIEWERS_ENDPOINT ?? 'https://playback-viewers.livepush.io'

const DEFAULT_TIMEOUT_MS = 7000

/** Sent with every authorize so the service can correlate player behaviour. */
const PLAYER_VERSION = '2.0.0'

async function getJSON<T>(url: string, timeoutMs = DEFAULT_TIMEOUT_MS): Promise<T | null> {
  try {
    const res = await fetch(url, { signal: AbortSignal.timeout(timeoutMs) })
    if (!res.ok) return null
    return (await res.json()) as T
  } catch {
    return null
  }
}

/* ── watch-guard: authorize ────────────────────────────────────────────── */

/**
 * Policy denials the service can return. They arrive as HTTP 200 with an
 * `error` field — a denial is an ANSWER, not a failure, and the player renders
 * each one as a branded state. Only transport failures and 4xx/5xx mean
 * "something broke".
 */
export type DenialReason =
  | 'password_required'
  | 'password_incorrect'
  | 'domain_not_allowed'
  | 'iframe_required'
  | 'iframe_forbidden'
  | 'geo_blocked'
  | 'ip_blocked'
  | 'concurrency_exceeded'
  | 'stream_disabled'

/** Denials that still carry a full config (vs branding-only) — see the design's
 *  leak rule: an unauthorised site must not learn the stream's content. */
const FULL_CONFIG_DENIALS = new Set<DenialReason>([
  'password_required',
  'password_incorrect',
  'geo_blocked',
  'ip_blocked',
  'concurrency_exceeded',
])

export function denialCarriesFullConfig(reason: DenialReason): boolean {
  return FULL_CONFIG_DENIALS.has(reason)
}

export interface PlaybackGrant {
  /** Tokenised manifest URL. NEVER log this: the token is a bearer credential. */
  hlsUrl: string
  sessionId: string
  /** ISO — the token stops validating at the edge after this */
  expiresAt: string
  /** ISO — renew any time after this, well before expiry */
  renewAfter: string | null
  /**
   * ISO — when the SESSION (not the token) stops being rejoinable.
   *
   * These are different clocks and conflating them is what makes a
   * password-protected embed ask twice. The token lives ~30 minutes; the
   * session is what lets a returning viewer skip the password. Optional: when
   * the service does not send it, ids.ts falls back to its own long default,
   * which errs on the side of trying to rejoin. That costs nothing if the
   * session is gone (watch-guard answers `password_required` and the gate
   * appears, exactly as it would have anyway), while erring short guarantees a
   * re-prompt the server never asked for.
   */
  sessionExpiresAt: string | null
}

/** Raw config object from the service; shaped and defaulted by config.ts. */
export type RawPlayerConfig = Record<string, unknown>

export type AuthorizeResult =
  | { ok: true; config: RawPlayerConfig; playback: PlaybackGrant }
  | {
      ok: false
      kind: 'denied'
      config: RawPlayerConfig
      reason: DenialReason
      message: string
      /** Present on 429 — how long the password form stays locked */
      retryAfterSeconds?: number
    }
  | { ok: false; kind: 'fault'; status: number | null }

export interface AuthorizeParams {
  /** Whether the player is inside an iframe. Self-reported: the server has no
   *  way to verify it, so this stops an embed being lifted onto another page,
   *  not a determined caller. */
  framed: boolean
  /**
   * Origin of the page we are embedded in, when it can be determined.
   *
   * The domain allowlist is judged on THIS, not on our own origin — the
   * authorize request carries `watch.livepush.io` whoever is watching and
   * wherever the iframe sits, so it cannot tell customer sites apart. Only
   * script inside the frame can see the parent, which is why the player has
   * to report it. Omitted when top-level or undeterminable.
   */
  referrerUrl?: string
  /** Viewer asked for the audio-only variant (?audio_only=true) */
  audioOnly?: boolean
  /** Only on a retry after `password_required`. Never stored anywhere. */
  password?: string
  /** Remembered session, so a returning viewer is not re-prompted */
  sessionId?: string | null
  playerVersion?: string
}

interface RawAuthorizeBody {
  config?: RawPlayerConfig
  playback?: Partial<PlaybackGrant>
  error?: string
  message?: string
}

/** POST + parse, keeping "policy said no" separate from "the call failed". */
async function postAuthorize(url: string, body: unknown): Promise<AuthorizeResult> {
  let res: Response
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
    })
  } catch {
    return { ok: false, kind: 'fault', status: null } // offline, DNS, timeout, CORS
  }

  let parsed: RawAuthorizeBody | null = null
  try {
    parsed = (await res.json()) as RawAuthorizeBody
  } catch {
    parsed = null
  }

  // 429 is a denial with a cooldown, not a fault: it is the rate limiter
  // answering a password attempt, and the viewer needs to see that.
  if (res.status === 429) {
    return {
      ok: false,
      kind: 'denied',
      config: parsed?.config ?? {},
      reason: 'password_incorrect',
      message: parsed?.message ?? 'Too many attempts. Please wait and try again.',
      retryAfterSeconds: retryAfterFrom(res),
    }
  }

  if (!res.ok) return { ok: false, kind: 'fault', status: res.status }

  if (parsed?.error) {
    return {
      ok: false,
      kind: 'denied',
      config: parsed.config ?? {},
      reason: parsed.error as DenialReason,
      message: parsed.message ?? '',
    }
  }

  const grant = parsed?.playback
  if (!grant?.hlsUrl || !grant.sessionId || !grant.hlsUrl.startsWith('http')) {
    // 200 with neither a grant nor a reason — treat as broken, not as allowed.
    return { ok: false, kind: 'fault', status: res.status }
  }

  return {
    ok: true,
    config: parsed?.config ?? {},
    playback: {
      hlsUrl: grant.hlsUrl,
      sessionId: grant.sessionId,
      expiresAt: grant.expiresAt ?? '',
      renewAfter: grant.renewAfter ?? null,
      sessionExpiresAt: grant.sessionExpiresAt ?? null,
    },
  }
}

function retryAfterFrom(res: Response): number {
  const header = res.headers.get('Retry-After')
  const seconds = header ? Number(header) : NaN
  return Number.isFinite(seconds) && seconds > 0 ? seconds : 60
}

/**
 * The boot call: one request returns both what the embed looks like and whether
 * this viewer may watch it.
 */
export function authorize(embedId: string, params: AuthorizeParams): Promise<AuthorizeResult> {
  return postAuthorize(`${WATCH_API}/playback/${encodeURIComponent(embedId)}`, {
    framed: params.framed,
    ...(params.referrerUrl ? { referrerUrl: params.referrerUrl } : {}),
    ...(params.audioOnly ? { audioOnly: true } : {}),
    ...(params.password ? { password: params.password } : {}),
    ...(params.sessionId ? { sessionId: params.sessionId } : {}),
    playerVersion: params.playerVersion ?? PLAYER_VERSION,
  })
}

/**
 * Re-authorise before the token expires. Policy is evaluated again, so this can
 * be DENIED — which means stop playback, not keep the old token.
 */
/* ── live presentation config ──────────────────────────────────────────── */

export type ConfigResult =
  | { kind: 'unchanged' }
  | { kind: 'config'; config: RawPlayerConfig; etag: string | null }
  | { kind: 'fault' }

/**
 * Poll an embed's presentation config.
 *
 * Public and per-embed rather than per-viewer, so it is edge-cacheable: one
 * response serves every viewer of the embed. Carries presentation only — never
 * a playback URL, a session, or anything policy depends on. Policy is per
 * viewer and arrives through renew and the viewer ping instead.
 *
 * Conditional on the ETag, so a poll that finds nothing changed costs a 304.
 * Faults are indistinguishable from "not deployed yet" on purpose: either way
 * the caller keeps the config it has.
 */
export async function fetchConfig(embedId: string, etag: string | null): Promise<ConfigResult> {
  let res: Response
  try {
    res = await fetch(`${WATCH_API}/embeds/${encodeURIComponent(embedId)}/config`, {
      headers: etag ? { 'If-None-Match': etag } : undefined,
      signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
    })
  } catch {
    return { kind: 'fault' }
  }

  if (res.status === 304) return { kind: 'unchanged' }
  if (!res.ok) return { kind: 'fault' }

  try {
    const body = (await res.json()) as { config?: RawPlayerConfig }
    if (!body?.config || typeof body.config !== 'object') return { kind: 'fault' }
    return { kind: 'config', config: body.config, etag: res.headers.get('ETag') }
  } catch {
    return { kind: 'fault' }
  }
}

export function renew(embedId: string, sessionId: string): Promise<AuthorizeResult> {
  return postAuthorize(`${WATCH_API}/playback/${encodeURIComponent(embedId)}/renew`, { sessionId })
}

export interface CastGrant {
  hlsUrl: string
  expiresAt: string
  castSessionId: string
}

/**
 * A token for a cast receiver: longer-lived and NOT IP-bound, because the
 * Apple TV or Chromecast fetches the manifest itself, from its own address.
 * Handing a receiver the browser's own URL is what used to break casting.
 */
export async function authorizeCast(
  embedId: string,
  sessionId: string,
  receiver: 'chromecast' | 'airplay',
): Promise<CastGrant | null> {
  let res: Response
  try {
    res = await fetch(`${WATCH_API}/playback/${encodeURIComponent(embedId)}/cast`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, receiver }),
      signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
    })
  } catch {
    return null
  }
  if (!res.ok) return null

  try {
    const body = (await res.json()) as Partial<CastGrant>
    if (!body.hlsUrl || !body.castSessionId) return null
    return {
      hlsUrl: body.hlsUrl,
      expiresAt: body.expiresAt ?? '',
      castSessionId: body.castSessionId,
    }
  } catch {
    return null
  }
}

/**
 * Live status, with "I could not ask" kept distinct from "it said no".
 *
 * Returning false for a failed request made every network blip — a sleeping
 * laptop, a dropped VPN, a 502, a timeout — indistinguishable from the stream
 * actually ending, and the player would show its offline screen for a stream
 * that never stopped.
 *
 * @returns true/false when the API answered, null when it could not be reached
 */
export async function fetchLiveStatus(streamId: string): Promise<boolean | null> {
  const body = await getJSON<{ isLive?: boolean }>(
    `${STATUS_API}/pulse/status?stream=${encodeURIComponent(streamId)}`,
  )
  if (body === null) return null
  return body.isLive === true
}

export interface ViewerParams {
  embedId: string
  nsid: string | null
  sessionId: string | null
}

function viewerQuery({ embedId, nsid, sessionId }: ViewerParams): string {
  const params = new URLSearchParams({ video: embedId, ns: nsid ?? 'default' })
  if (sessionId) params.set('session_id', sessionId)
  return params.toString()
}

const DENIAL_REASONS: ReadonlySet<string> = new Set<DenialReason>([
  'password_required',
  'password_incorrect',
  'domain_not_allowed',
  'iframe_required',
  'iframe_forbidden',
  'geo_blocked',
  'ip_blocked',
  'concurrency_exceeded',
  'stream_disabled',
])

/**
 * The liveness ping doubles as the revocation channel.
 *
 * It already runs every few seconds per viewer to count concurrency, so it is
 * the one call that is both per-viewer and frequent — which makes it the right
 * place to learn that access has been withdrawn. Without it a disabled embed
 * keeps playing until the token expires, up to an hour later.
 *
 * Advisory only: renew remains the authority. An unreachable ping returns null
 * and the viewer keeps playing on a token that is still valid, which is the
 * correct way to fail.
 */
export async function pingViewer(p: ViewerParams): Promise<DenialReason | null> {
  const body = await getJSON<{ revoked?: string }>(`${VIEWERS_API}/ping?${viewerQuery(p)}`)
  const revoked = body?.revoked
  return typeof revoked === 'string' && DENIAL_REASONS.has(revoked) ? (revoked as DenialReason) : null
}

export async function fetchViewerCount(p: ViewerParams): Promise<number | null> {
  const body = await getJSON<{ live_viewers?: number; viewers_count?: number }>(
    `${VIEWERS_API}/count?${viewerQuery(p)}`,
  )
  const count = Number(body?.live_viewers ?? body?.viewers_count)
  return Number.isFinite(count) ? count : null
}

const FEEDBACK_API = import.meta.env.VITE_FEEDBACK_ENDPOINT ?? ''

export interface PlayerFeedback {
  kind: 'feedback' | 'report'
  reason: string
  embedId: string
  sessionId: string | null
}

/** Best-effort viewer feedback/report submission. No-op (logged) until the
 *  backend endpoint is configured via VITE_FEEDBACK_ENDPOINT. */
export async function submitPlayerFeedback(payload: PlayerFeedback): Promise<void> {
  const body = { ...payload, ts: Date.now(), ua: navigator.userAgent }
  if (!FEEDBACK_API) {
    console.debug('[player] feedback endpoint not configured — captured locally only', body)
    return
  }
  try {
    await fetch(FEEDBACK_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
    })
  } catch {
    /* best-effort */
  }
}

/**
 * Recurring poller with jitter and exponential backoff on failure.
 * Returns a stop function. The next tick is only scheduled after the previous
 * one finishes — overlapping requests are impossible by construction.
 */
export function startPoller(
  task: () => Promise<boolean | void>,
  { intervalMs, maxBackoffMs = intervalMs * 8 }: { intervalMs: number; maxBackoffMs?: number },
): () => void {
  let stopped = false
  let timer: ReturnType<typeof setTimeout> | undefined
  let currentInterval = intervalMs

  const tick = async () => {
    if (stopped) return
    let ok: boolean | void = true
    try {
      ok = await task()
    } catch {
      ok = false
    }
    currentInterval = ok === false ? Math.min(currentInterval * 2, maxBackoffMs) : intervalMs
    if (stopped) return
    const jitter = currentInterval * 0.15 * Math.random()
    timer = setTimeout(tick, currentInterval + jitter)
  }

  void tick()

  return () => {
    stopped = true
    if (timer) clearTimeout(timer)
  }
}
